import type * as THREE from 'three';

/** One switchable part of the frame: `off` removes it, `on` restores it. */
export interface Variant { name: string; off(): void; on(): void }

/**
 * `?profile`: measures what each part of the frame costs on the GPU of the device it runs on.
 * It renders the baseline, then each variant switched off, alternating several rounds, timing
 * only the GPU work of the render with EXT_disjoint_timer_query_webgl2, and prints a table of
 * medians (overlay + console). Must run in a visible tab — browsers throttle hidden ones.
 */
export class Profiler {
  private readonly gl: WebGL2RenderingContext;
  private readonly ext: { TIME_ELAPSED_EXT: number; GPU_DISJOINT_EXT: number } | null;
  private readonly el: HTMLDivElement;
  private readonly pending: { q: WebGLQuery; slot: number }[] = [];
  private active: WebGLQuery | null = null;
  /** -1 = baseline, i = variants[i] off */
  private readonly schedule: number[] = [];
  private readonly samples = new Map<number, number[]>();
  private step = 0; private frame = 0; private warm = 0;
  done = false;

  constructor(renderer: THREE.WebGLRenderer, private readonly variants: Variant[], rounds = 4, private readonly perSlot = 24) {
    this.gl = renderer.getContext() as WebGL2RenderingContext;
    this.ext = this.gl.getExtension('EXT_disjoint_timer_query_webgl2');
    for (let r = 0; r < rounds; r++) for (let i = -1; i < variants.length; i++) this.schedule.push(i);
    this.el = document.createElement('div');
    Object.assign(this.el.style, { position: 'fixed', right: '8px', top: '8px', padding: '8px 10px', font: '11px/1.4 ui-monospace,monospace', color: '#dfe', background: 'rgba(0,0,0,.72)', pointerEvents: 'none', whiteSpace: 'pre', zIndex: '20', borderRadius: '6px' });
    document.body.appendChild(this.el);
    this.el.textContent = this.ext ? 'profiling… keep this tab visible' : 'GPU timer queries unavailable here';
    if (!this.ext) this.done = true;
    else this.apply();
  }

  private get slot() { return this.schedule[this.step]; }
  private apply() { const s = this.slot; if (s >= 0) this.variants[s].off(); this.warm = 8; this.frame = 0; }
  private restore() { const s = this.slot; if (s >= 0) this.variants[s].on(); }

  begin() {
    if (this.done || this.active) return;
    if (this.warm > 0) { this.warm--; return; }
    this.active = this.gl.createQuery()!;
    this.gl.beginQuery(this.ext!.TIME_ELAPSED_EXT, this.active);
  }

  end() {
    if (this.done) return;
    const gl = this.gl;
    if (this.active) { gl.endQuery(this.ext!.TIME_ELAPSED_EXT); this.pending.push({ q: this.active, slot: this.slot }); this.active = null; this.frame++; }
    while (this.pending.length && gl.getQueryParameter(this.pending[0].q, gl.QUERY_RESULT_AVAILABLE)) {
      const { q, slot } = this.pending.shift()!;
      if (!gl.getParameter(this.ext!.GPU_DISJOINT_EXT)) {
        const list = this.samples.get(slot) ?? []; list.push(gl.getQueryParameter(q, gl.QUERY_RESULT) / 1e6); this.samples.set(slot, list);
      }
      gl.deleteQuery(q);
    }
    if (this.frame < this.perSlot) return;
    this.restore();
    if (++this.step >= this.schedule.length) { this.report(); return; }
    this.el.textContent = `profiling… ${Math.round(this.step / this.schedule.length * 100)}%  keep this tab visible`;
    this.apply();
  }

  private report() {
    this.done = true;
    const med = (a: number[] = []) => { const s = [...a].sort((x, y) => x - y); return s.length ? s[s.length >> 1] : NaN; };
    const base = med(this.samples.get(-1));
    const rows = this.variants.map((v, i) => { const off = med(this.samples.get(i)); return { part: v.name, ms: +(base - off).toFixed(2), share: `${Math.round((base - off) / base * 100)}%` }; })
      .sort((a, b) => b.ms - a.ms);
    console.table([{ part: 'whole frame (GPU)', ms: +base.toFixed(2), share: '100%' }, ...rows]);
    this.el.textContent = `GPU frame ${base.toFixed(2)} ms  (cost = baseline − part off)\n` +
      rows.map((r) => `${r.part.padEnd(22)} ${r.ms.toFixed(2).padStart(6)} ms  ${r.share.padStart(4)}`).join('\n');
  }
}
